import { createContext, useState } from 'react';
import { useQuery } from 'react-query';
import { AuthContextType, User, UserPreferences } from '../models/common-types';
import api from '../api/api';
import { isNumber } from 'lodash';
import { PLATFORM_APP } from '../constants/constants';

const AuthContextProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState<User>();
    const [preferences, setPreferences] = useState<UserPreferences>(null);

    const { data: refreshTokenData } = useQuery('refreshToken', api.authentication.getRefreshToken, {
        refetchIntervalInBackground: true,
        enabled: !PLATFORM_APP,
        refetchInterval: (data) => {
            const expiresIn = data?.expiresIn;
            return isNumber(expiresIn) ? (expiresIn / 2) * 1000 : 150 * 1000;
        },
    });

    useQuery('userDetails', api.authentication.getUserDetails, {
        enabled: PLATFORM_APP || !!refreshTokenData,
        onSuccess: async (data) => {
            if (data) {
                await getUserPreferences();
                localStorage.setItem('userId', data.id);
                window.pendo.initialize({
                    visitor: {
                        id: data.id,
                        role: data.role,
                        app_name: 'CSS',
                        environment: 'DEV',
                    },
                    account: {
                        id: document.domain,
                        name: document.domain,
                    },
                });
                setUser(data);
                setIsAuthenticated(true);
            }
        },
        onError() {
            logout();
        },
    });

    const getUserPreferences = async () => {
        const res = await api.authentication.getUserPreferences();
        const storedReason = localStorage.getItem('searchReason');
        setPreferences(res);
        if (!res.searchReasons?.find((reason) => reason.label === storedReason)) {
            localStorage.setItem('searchReason', res.searchReasons[0].label);
        }
    };

    const addSearchReason = async (searchReason) => {
        const preferencesToUpdate = { ...preferences };
        preferencesToUpdate.searchReasons = preferencesToUpdate.searchReasons.filter((reason) => !reason.default);
        preferencesToUpdate.searchReasons.push(searchReason);
        const res = await api.authentication.updateUserPreferences(preferencesToUpdate);

        setPreferences(res);
        return Boolean(res);
    };

    const deleteSearchReason = async (searchReason) => {
        setPreferences((prev) => ({
            ...prev,
            searchReasons: prev.searchReasons.filter((reason) => reason.label !== searchReason),
        }));
        const preferencesToUpdate = { ...preferences };
        preferencesToUpdate.searchReasons = preferencesToUpdate.searchReasons.filter(
            (reason) => reason.label !== searchReason,
        );
        preferencesToUpdate.searchReasons = preferencesToUpdate.searchReasons.filter((reason) => !reason.default);
        const res = await api.authentication.updateUserPreferences(preferencesToUpdate);
        setPreferences(res);
        return Boolean(res);
    };

    const logout = async () => {
        await api.authentication.logout();
        setIsAuthenticated(false);
    };

    const isUserAdmin = () => {
        return !!user && user.role === 'Administrator';
    };

    if (!isAuthenticated) {
        return null;
    }

    return (
        <AuthContext.Provider
            value={{
                isAuthenticated,
                user,
                preferences,
                logout,
                addSearchReason,
                deleteSearchReason,
                isUserAdmin,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const AuthContext = createContext({} as AuthContextType);
export default AuthContextProvider;
